/**
 * Cherry LA - Redo Shopify Toggle Adapter
 * Converts <redo-shopify-toggle>'s closed shadow root into a single-button toggle system
 * styled identically to .cherry-cart__protection.
 */
(() => {
  const STORAGE_KEY = 'cherry_redo_protection_active';
  const CART_PAGE_STORAGE_KEY = 'cherry_redo_cart_page_protection_active';
  const subtotalPlacements = new Map();
  const adapterScriptUrl = document.currentScript?.src || document.baseURI;
  const protectionInfoUrl = new URL('cherry-cart-protection-info.png', adapterScriptUrl).href;
  const protectionKnobUrl = new URL('cherry-cart-protection-knob.svg', adapterScriptUrl).href;

  function restoreDetachedSubtotals() {
    subtotalPlacements.forEach(({ holder, marker, originalSlot }, host) => {
      if (host.isConnected && host.contains(holder)) return;
      if (marker.isConnected) {
        if (originalSlot === null) holder.removeAttribute('slot');
        else holder.setAttribute('slot', originalSlot);
        marker.replaceWith(holder);
      }
      subtotalPlacements.delete(host);
    });
  }

  const getStorageKey = (el) => el?.closest('[data-cart-page]')
    ? CART_PAGE_STORAGE_KEY
    : STORAGE_KEY;

  const getSavedPreference = (el) => {
    try {
      const val = localStorage.getItem(getStorageKey(el));
      return val === null ? true : val === 'true';
    } catch (e) {
      return true;
    }
  };

  const savePreference = (el, val) => {
    try {
      localStorage.setItem(getStorageKey(el), String(val));
    } catch (e) {}
  };

  function parseMoneyToCents(value) {
    const numeric = String(value || '').replace(/[^\d.,-]/g, '');
    if (!numeric) return 0;

    const separatorIndex = Math.max(numeric.lastIndexOf('.'), numeric.lastIndexOf(','));
    const fractionLength = separatorIndex === -1 ? 0 : numeric.length - separatorIndex - 1;
    let normalized;

    if (separatorIndex !== -1 && fractionLength > 0 && fractionLength <= 2) {
      const whole = numeric.slice(0, separatorIndex).replace(/[.,]/g, '') || '0';
      const fraction = numeric.slice(separatorIndex + 1).replace(/[.,]/g, '');
      normalized = `${whole}.${fraction}`;
    } else {
      normalized = numeric.replace(/[.,]/g, '');
    }

    const amount = Number(normalized);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function formatMoney(cents) {
    const currencyCodeEnabled = Boolean(window.theme?.settings?.currency_code_enable);
    const format = currencyCodeEnabled
      ? window.theme?.moneyWithCurrencyFormat
      : window.theme?.moneyFormat;
    const formatter = window.themeVendor?.themeCurrency?.formatMoney
      || window.themeVendor?.Currency?.formatMoney;

    let formatted = formatter
      ? formatter(cents, format)
      : `$${(cents / 100).toFixed(2)}`;

    if (!currencyCodeEnabled && cents % 100 === 0) {
      formatted = formatted.replace(/([.,]00)(?!\d)/, '');
    }

    return formatted;
  }

  function updateCartPageSubtotal(el, active) {
    const cartPage = el.closest('[data-cart-page]');
    if (!cartPage) return;

    const subtotalDisplay = cartPage.querySelector('.cherry-cart-page__subtotal [data-cart-total-price]');
    const baseSubtotal = cartPage.querySelector('[data-cart-price-holder] [data-cart-total]');
    const shadow = el.shadowRoot || el._redoShadowRoot;
    const protectionPrice = shadow?.querySelector('[data-target="price"]')
      || shadow?.querySelector('.cherry-redo-toggle-price');
    if (!subtotalDisplay || !baseSubtotal) return;

    const baseCents = Number(baseSubtotal.getAttribute('data-cart-total'));
    if (!Number.isFinite(baseCents)) return;

    const protectionCents = parseMoneyToCents(protectionPrice?.textContent);
    const totalCents = baseCents + (active ? protectionCents : 0);
    const baseText = baseCents === 0
      ? (window.theme?.strings?.free || baseSubtotal.textContent.trim())
      : formatMoney(baseCents);
    const nextText = active && protectionCents > 0
      ? formatMoney(totalCents)
      : baseText;

    if (subtotalDisplay.textContent.trim() !== nextText) {
      subtotalDisplay.textContent = nextText;
    }

    subtotalDisplay.dataset.cartBaseSubtotal = String(baseCents);
    subtotalDisplay.dataset.cartProtectionPrice = String(protectionCents);
  }

  const SHADOW_CSS = `
    [data-target="info-card-container"] {
      display: none !important;
    }

    .cherry-redo-toggle-row {
      display: grid !important;
      grid-template-columns: minmax(0, auto) 24px 1fr !important;
      gap: 10px !important;
      align-items: center !important;
      width: 100% !important;
      min-height: 54px !important;
      padding: 10px 0 !important;
      margin-bottom: 12px !important;
      border-bottom: 0.5px solid #e3e3e1 !important;
      box-sizing: border-box !important;
      font-family: inherit !important;
    }

    .cherry-redo-toggle-control {
      display: contents !important;
    }

    .cherry-redo-toggle-label {
      display: flex !important;
      align-items: center !important;
      gap: 10px !important;
      min-width: 0 !important;
      margin: 0 !important;
      color: #241a12 !important;
      cursor: pointer !important;
      font-size: 13px !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
      letter-spacing: 0.7px !important;
      white-space: nowrap !important;
      user-select: none !important;
      -webkit-user-select: none !important;
    }

    .cherry-redo-toggle-input {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0 0 0 0) !important;
      opacity: 0 !important;
    }

    .cherry-redo-toggle-switch {
      position: relative !important;
      display: block !important;
      flex: 0 0 44px !important;
      width: 44px !important;
      height: 22px !important;
      background: #e3e3e1 !important;
      border-radius: 30px !important;
      transition: background-color 0.2s ease !important;
      cursor: pointer !important;
    }

    .cherry-redo-toggle-switch::after {
      position: absolute !important;
      top: 3px !important;
      left: 3px !important;
      width: 16px !important;
      height: 16px !important;
      background: #fffbf6 !important;
      border-radius: 50% !important;
      content: "" !important;
      transition: transform 0.2s ease !important;
    }

    .cherry-redo-toggle-input:checked + .cherry-redo-toggle-switch {
      background: #241a12 !important;
    }

    .cherry-redo-toggle-input:checked + .cherry-redo-toggle-switch::after {
      transform: translateX(22px) !important;
    }

    .cherry-redo-toggle-input:focus-visible + .cherry-redo-toggle-switch {
      outline: 2px solid #bd2023 !important;
      outline-offset: 2px !important;
    }

    .cherry-redo-toggle-info {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 16px !important;
      height: 16px !important;
      padding: 0 !important;
      color: #fffbf6 !important;
      background: #241a12 !important;
      border: 0 !important;
      border-radius: 50% !important;
      font-family: serif !important;
      font-size: 11px !important;
      font-weight: 700 !important;
      line-height: 1 !important;
      cursor: pointer !important;
      text-decoration: none !important;
    }

    .cherry-redo-toggle-price {
      justify-self: end !important;
      color: #241a12 !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      line-height: 1.5 !important;
      letter-spacing: 0.7px !important;
      white-space: nowrap !important;
    }

    [data-target="checkout-buttons-container"] {
      display: flex !important;
      flex-direction: column !important;
      width: 100% !important;
      margin-top: 0 !important;
      padding: 0 !important;
      gap: 0 !important;
    }

    slot[name="cherry-cart-subtotal"] {
      display: block;
      width: 100%;
    }

    [data-target="coverage-button"],
    [data-target="non-coverage-button"] {
      width: 100% !important;
      height: 50px !important;
      min-height: 50px !important;
      max-height: 50px !important;
      padding: 8px 20px !important;
      background-color: #241a12 !important;
      color: #fffbf6 !important;
      border: 0 !important;
      border-radius: 5px !important;
      font-family: inherit !important;
      font-size: 12px !important;
      font-weight: 400 !important;
      letter-spacing: 0.6px !important;
      text-transform: uppercase !important;
      cursor: pointer !important;
      box-sizing: border-box !important;
      text-decoration: none !important;
      margin: 0 !important;
      transition: opacity 0.2s ease !important;
      align-items: center !important;
      justify-content: center !important;
    }

    [data-target="coverage-button"]:hover,
    [data-target="non-coverage-button"]:hover {
      opacity: 0.88 !important;
    }

    [data-target="coverage-button"] *,
    [data-target="non-coverage-button"] * {
      color: #fffbf6 !important;
      font-family: inherit !important;
      font-size: 12px !important;
      font-weight: 400 !important;
      letter-spacing: 0.6px !important;
      text-transform: uppercase !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    [data-target="coverage-button"] [data-target="subtitle-text-container"],
    [data-target="coverage-button"] [data-target="subtitle-text-container"] * {
      display: none !important;
    }

    /* Single Button Rule: Show only ONE button at a time */
    :host(:not([data-protection-active="false"])) [data-target="coverage-button"] {
      display: flex !important;
    }
    :host(:not([data-protection-active="false"])) [data-target="non-coverage-button"] {
      display: none !important;
    }

    :host([data-protection-active="false"]) [data-target="coverage-button"] {
      display: none !important;
    }
    :host([data-protection-active="false"]) [data-target="non-coverage-button"] {
      display: flex !important;
    }

    /* Cart page mobile — Figma node 1047:1281. */
    @media (max-width: 1199px) {
      :host([data-cherry-cart-page]) .cherry-redo-toggle-row {
        grid-template-columns: minmax(0, auto) 20px minmax(32px, 1fr) !important;
        column-gap: 10px !important;
        width: 100% !important;
        height: 60px !important;
        min-height: 60px !important;
        margin: 0 !important;
        padding: 0 16px !important;
        border-top: 0.5px solid #e3e3e1 !important;
        border-bottom: 0.5px solid #e3e3e1 !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-label {
        gap: 10px !important;
        overflow: hidden !important;
        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: 1.5 !important;
        letter-spacing: 0.6px !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-text {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-switch {
        flex-basis: 50px !important;
        width: 50px !important;
        height: 20px !important;
        background: transparent !important;
        border: 0.5px solid #e3e3e1 !important;
        box-sizing: border-box !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-switch::after {
        top: 2px !important;
        left: 3px !important;
        width: 15px !important;
        height: 15px !important;
        background-color: #e3e3e1 !important;
        background-image: var(--cherry-redo-knob-image) !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        background-size: 15px 15px !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-input:checked + .cherry-redo-toggle-switch {
        background: #241a12 !important;
        border-color: #241a12 !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-input:checked + .cherry-redo-toggle-switch::after {
        background-color: #fffbf6 !important;
        background-image: none !important;
        transform: translateX(28px) !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-info {
        width: 20px !important;
        height: 20px !important;
        color: transparent !important;
        background-color: transparent !important;
        background-image: var(--cherry-redo-info-image) !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        background-size: 20px 20px !important;
        border-radius: 0 !important;
        font-size: 0 !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-price {
        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: 1.5 !important;
        letter-spacing: 0.6px !important;
      }

      :host([data-cherry-cart-page]) [data-target="checkout-buttons-container"] {
        width: calc(100% - 32px) !important;
        margin: 0 16px !important;
      }

      :host([data-cherry-cart-page]) [data-target="coverage-button"],
      :host([data-cherry-cart-page]) [data-target="non-coverage-button"] {
        position: relative !important;
        color: transparent !important;
        font-size: 0 !important;
        line-height: 0 !important;
      }

      :host([data-cherry-cart-page]) [data-target="coverage-button"] > *,
      :host([data-cherry-cart-page]) [data-target="non-coverage-button"] > * {
        visibility: hidden !important;
      }

      :host([data-cherry-cart-page]) [data-target="coverage-button"]::after,
      :host([data-cherry-cart-page]) [data-target="non-coverage-button"]::after {
        position: absolute !important;
        inset: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        color: #fffbf6 !important;
        content: "PROCEED TO CHECKOUT";
        font-family: inherit !important;
        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: 1.5 !important;
        letter-spacing: 0.6px !important;
        white-space: nowrap !important;
        visibility: visible !important;
      }
    }

    /* The cart page uses the measurements from Figma node 1047:1548. */
    @media (min-width: 1200px) {
      :host([data-cherry-cart-page]) {
        container-type: inline-size;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-row {
        grid-template-columns: minmax(0, 1fr) auto !important;
        column-gap: 10px !important;
        min-height: 21px !important;
        padding: 0 !important;
        margin: 40px 0 35px !important;
        border-bottom: 0 !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-control {
        display: flex !important;
        align-items: center !important;
        gap: 10px !important;
        min-width: 0 !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-label {
        justify-content: flex-start !important;
        overflow: hidden !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: 1.5 !important;
        letter-spacing: 0.7px !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-text {
        min-width: 0 !important;
        overflow: hidden !important;
        font-size: 0 !important;
        text-overflow: clip !important;
        white-space: nowrap !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-text::after {
        content: "Get free returns + package protection";
        font-size: 14px !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-switch {
        flex-basis: 50px !important;
        width: 50px !important;
        height: 20px !important;
        background: transparent !important;
        border: 0.5px solid #e3e3e1 !important;
        box-sizing: border-box !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-switch::after {
        top: 2px !important;
        left: 3px !important;
        width: 15px !important;
        height: 15px !important;
        background-color: #e3e3e1 !important;
        background-image: var(--cherry-redo-knob-image) !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        background-size: 15px 15px !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-input:checked + .cherry-redo-toggle-switch {
        background: #241a12 !important;
        border-color: #241a12 !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-input:checked + .cherry-redo-toggle-switch::after {
        background-color: #fffbf6 !important;
        background-image: none !important;
        transform: translateX(28px) !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-info {
        width: 20px !important;
        height: 20px !important;
        color: transparent !important;
        background-color: transparent !important;
        background-image: var(--cherry-redo-info-image) !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        background-size: 20px 20px !important;
        border-radius: 0 !important;
        font-size: 0 !important;
      }

      :host([data-cherry-cart-page]) .cherry-redo-toggle-price {
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: 1.5 !important;
        letter-spacing: 0.7px !important;
      }

      :host([data-cherry-cart-page]) [data-target="coverage-button"],
      :host([data-cherry-cart-page]) [data-target="non-coverage-button"] {
        position: relative !important;
        color: transparent !important;
        font-size: 0 !important;
        line-height: 0 !important;
      }

      :host([data-cherry-cart-page]) [data-target="coverage-button"] > *,
      :host([data-cherry-cart-page]) [data-target="non-coverage-button"] > * {
        visibility: hidden !important;
      }

      :host([data-cherry-cart-page]) [data-target="coverage-button"]::after,
      :host([data-cherry-cart-page]) [data-target="non-coverage-button"]::after {
        position: absolute !important;
        inset: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        color: #fffbf6 !important;
        content: "PROCEED TO CHECKOUT";
        font-family: inherit !important;
        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: 1.5 !important;
        letter-spacing: 0.6px !important;
        white-space: nowrap !important;
        visibility: visible !important;
      }

      @container (max-width: 420px) {
        :host([data-cherry-cart-page]) .cherry-redo-toggle-row {
          column-gap: 6px !important;
        }

        :host([data-cherry-cart-page]) .cherry-redo-toggle-label {
          gap: 8px !important;
        }

        :host([data-cherry-cart-page]) .cherry-redo-toggle-control {
          gap: 6px !important;
        }
      }
    }
  `;

  function updateCheckoutButtonAccess(shadow, active) {
    const choices = [
      [shadow.querySelector('[data-target="coverage-button"]'), active],
      [shadow.querySelector('[data-target="non-coverage-button"]'), !active]
    ];

    choices.forEach(([button, selected]) => {
      if (!button) return;

      const hiddenValue = String(!selected);
      if (button.getAttribute('aria-hidden') !== hiddenValue) {
        button.setAttribute('aria-hidden', hiddenValue);
      }

      if (selected) {
        if (button.getAttribute('tabindex') === '-1') button.removeAttribute('tabindex');
      } else if (button.getAttribute('tabindex') !== '-1') {
        button.setAttribute('tabindex', '-1');
      }
    });
  }

  function updateToggleState(el, active) {
    if (el.getAttribute('data-protection-active') !== String(active)) {
      el.setAttribute('data-protection-active', String(active));
    }

    const shadow = el.shadowRoot || el._redoShadowRoot;
    if (!shadow) return;

    const input = shadow.querySelector('.cherry-redo-toggle-input');
    if (input && input.checked !== active) input.checked = active;
    updateCheckoutButtonAccess(shadow, active);
    updateCartPageSubtotal(el, active);
  }

  function sharesToggleScope(source, candidate) {
    const sourceCartPage = source.closest('[data-cart-page]');
    const candidateCartPage = candidate.closest('[data-cart-page]');

    if (sourceCartPage) return sourceCartPage === candidateCartPage;
    return !candidateCartPage;
  }

  function setupRedoElement(el) {
    if (!el) return;
    const shadow = el.shadowRoot || el._redoShadowRoot;
    if (!shadow) return;

    let isUpdating = false;

    function applyToggle() {
      if (isUpdating) return;
      isUpdating = true;

      try {
        // Ensure our style element is present in the shadow root
        let style = shadow.getElementById('cherry-redo-style');
        if (!style) {
          style = document.createElement('style');
          style.id = 'cherry-redo-style';
          style.textContent = SHADOW_CSS;
          shadow.prepend(style);
        }

        const buttonsContainer = shadow.querySelector('[data-target="checkout-buttons-container"]');
        if (!buttonsContainer) {
          isUpdating = false;
          return;
        }

        const cartPage = el.closest('[data-cart-page]');
        el.toggleAttribute('data-cherry-cart-page', Boolean(cartPage));
        el.style.setProperty('--cherry-redo-info-image', `url("${protectionInfoUrl}")`);
        el.style.setProperty('--cherry-redo-knob-image', `url("${protectionKnobUrl}")`);
        if (cartPage) {
          buttonsContainer.querySelectorAll('[data-target="coverage-button"], [data-target="non-coverage-button"]').forEach((button) => {
            button.setAttribute('aria-label', 'Proceed to checkout');
          });
        }

        // Set initial host attribute based on saved preference
        const currentActive = getSavedPreference(el);
        if (!el.hasAttribute('data-protection-active')) {
          el.setAttribute('data-protection-active', String(currentActive));
        }

        // Create or get the toggle row
        let toggleRow = shadow.querySelector('[data-cherry-toggle]');
        if (!toggleRow) {
          toggleRow = document.createElement('div');
          toggleRow.className = 'cherry-redo-toggle-row';
          toggleRow.setAttribute('data-cherry-toggle', '');
          toggleRow.innerHTML = `
            <div class="cherry-redo-toggle-control">
              <label class="cherry-redo-toggle-label">
                <input type="checkbox" class="cherry-redo-toggle-input">
                <span class="cherry-redo-toggle-switch" aria-hidden="true"></span>
                <span class="cherry-redo-toggle-text">Get free returns + package protection</span>
              </label>
              <button type="button" class="cherry-redo-toggle-info" aria-label="More information about coverage" title="Package protection information">i</button>
            </div>
            <span class="cherry-redo-toggle-price"></span>
          `;

          // Insert right above the buttons container
          buttonsContainer.parentNode.insertBefore(toggleRow, buttonsContainer);

          const input = toggleRow.querySelector('.cherry-redo-toggle-input');
          input.checked = el.getAttribute('data-protection-active') !== 'false';

          input.addEventListener('change', () => {
            const active = input.checked;
            savePreference(el, active);

            // Keep only toggles in the same cart surface synchronized. The
            // cart page must not mutate the cart drawer's independent state.
            syncAllToggles(active, el);
          });

          const infoBtn = toggleRow.querySelector('.cherry-redo-toggle-info');
          infoBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const originalInfoBtn = shadow.querySelector('[data-target="toggle-info-button"]');
            if (originalInfoBtn) {
              originalInfoBtn.click();
            }
          });
        }

        // Project the live subtotal between coverage and checkout while keeping
        // it in the light DOM for the theme's cart price updates.
        const drawer = el.closest('.cart-drawer');
        const subtotalHolder = drawer?.querySelector('[data-cart-price-holder]')
          || cartPage?.querySelector('.cherry-cart-page__subtotal');
        if (subtotalHolder) {
          let subtotalSlot = shadow.querySelector('slot[name="cherry-cart-subtotal"]');
          if (!subtotalSlot) {
            subtotalSlot = document.createElement('slot');
            subtotalSlot.name = 'cherry-cart-subtotal';
          }
          if (subtotalSlot.nextSibling !== buttonsContainer) {
            buttonsContainer.before(subtotalSlot);
          }
          if (subtotalHolder.parentNode !== el) {
            const marker = document.createComment('Cart subtotal original position');
            const originalSlot = subtotalHolder.getAttribute('slot');
            subtotalHolder.before(marker);
            subtotalPlacements.set(el, { holder: subtotalHolder, marker, originalSlot });
            subtotalHolder.slot = 'cherry-cart-subtotal';
            el.appendChild(subtotalHolder);
          }
        }

        // Keep input checked state in sync with host attribute
        const input = toggleRow.querySelector('.cherry-redo-toggle-input');
        if (input) {
          const shouldBeChecked = el.getAttribute('data-protection-active') !== 'false';
          if (input.checked !== shouldBeChecked) {
            input.checked = shouldBeChecked;
          }
          updateCheckoutButtonAccess(shadow, shouldBeChecked);
        }

        // Sync price from Redo's price element
        const redoPrice = shadow.querySelector('[data-target="price"]');
        const priceDisplay = toggleRow.querySelector('.cherry-redo-toggle-price');
        if (redoPrice && priceDisplay) {
          const priceText = redoPrice.textContent.trim();
          if (priceText && priceDisplay.textContent !== priceText) {
            priceDisplay.textContent = priceText;
          }
        }

        updateCartPageSubtotal(
          el,
          el.getAttribute('data-protection-active') !== 'false'
        );
      } finally {
        isUpdating = false;
      }
    }

    applyToggle();

    // Observe shadow root mutations for dynamic price or re-renders by Redo
    if (!el._cherryObserver) {
      const observer = new MutationObserver(() => {
        applyToggle();
      });
      observer.observe(shadow, {
        childList: true,
        subtree: true,
        characterData: true
      });
      el._cherryObserver = observer;
    }
  }

  function syncAllToggles(active, source) {
    document.querySelectorAll('redo-shopify-toggle').forEach((other) => {
      if (!sharesToggleScope(source, other)) return;
      updateToggleState(other, active);
    });

    // The legacy light-DOM preference belongs to the cart drawer only.
    if (source.closest('[data-cart-page]')) return;

    const lightCheckbox = document.getElementById('cherry-cart-protection');
    if (lightCheckbox && lightCheckbox.checked !== active) {
      lightCheckbox.checked = active;
      lightCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function scanAndInit() {
    restoreDetachedSubtotals();
    document.querySelectorAll('redo-shopify-toggle').forEach((el) => {
      setupRedoElement(el);
    });
  }

  // Listen to the early attachShadow event from head hook
  window.addEventListener('redo:shadow-attached', (e) => {
    if (e.detail?.element) {
      setupRedoElement(e.detail.element);
    }
  });

  // Observe document for dynamically added redo elements
  if (document.body) {
    const docObserver = new MutationObserver((mutations) => {
      restoreDetachedSubtotals();
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.tagName && node.tagName.toLowerCase() === 'redo-shopify-toggle') {
            setupRedoElement(node);
          } else if (node.querySelector) {
            const found = node.querySelector('redo-shopify-toggle');
            if (found) setupRedoElement(found);
          }
        }
      }
    });

    docObserver.observe(document.body, { childList: true, subtree: true });
    scanAndInit();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      const docObserver = new MutationObserver((mutations) => {
        restoreDetachedSubtotals();
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== Node.ELEMENT_NODE) continue;
            if (node.tagName && node.tagName.toLowerCase() === 'redo-shopify-toggle') {
              setupRedoElement(node);
            } else if (node.querySelector) {
              const found = node.querySelector('redo-shopify-toggle');
              if (found) setupRedoElement(found);
            }
          }
        }
      });

      docObserver.observe(document.body, { childList: true, subtree: true });
      scanAndInit();
    });
  }

  // Also scan on theme cart events
  ['theme:cart:loaded', 'theme:cart:added', 'cart:updated'].forEach((evt) => {
    document.addEventListener(evt, () => setTimeout(scanAndInit, 100));
  });
})();
